import { useMemo } from 'react';

import { VaultType, type ListVaultsParams } from '@/modules/api/codegen';

import type { VaultsTableRow } from '../types';
import { selectPersonalVaultsPage } from '../utils';
import { usePersonalVaultsAdministration } from './usePersonalVaultsAdministration';
import { useListVaultsQuery } from './useVaults';
import { useVaultsTableStore } from './useVaultsTableStore';

export const useVaultsTable = () => {
    const { filters, pagination, sort } = useVaultsTableStore();

    const params = useMemo<ListVaultsParams>(
        () => ({ type: VaultType.team, ...filters, ...pagination, ...sort }),
        [filters, pagination, sort],
    );

    const { query: teamQuery, vaults: teamVaults, total: teamTotal } = useListVaultsQuery(params);
    const { query: personalQuery, personalVaults } = usePersonalVaultsAdministration();

    const { rows, total } = useMemo<{ rows: VaultsTableRow[]; total: number }>(() => {
        const personal = selectPersonalVaultsPage(personalVaults, { filters, sort, pagination, teamTotal });

        // Until the team page is in, `teamTotal` is 0 and the personal vaults would take its place.
        const hasTeamPage = teamQuery.data !== undefined;

        return {
            rows: hasTeamPage ? [...teamVaults, ...personal.rows] : teamVaults,
            total: teamTotal + personal.total,
        };
    }, [personalVaults, filters, sort, pagination, teamTotal, teamQuery.data, teamVaults]);

    return {
        rows,
        total,
        isLoading: teamQuery.isLoading || personalQuery.isLoading,
        isError: teamQuery.isError || personalQuery.isError,
    };
};
